import { memo, useCallback, useRef, useState } from 'react'

import { FilterList } from '@mui/icons-material'
import { IconButton } from '@mui/material'
import { AudioObject } from '@nativewaves/nw-cloud-sdk/content'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { Table as ReactTableType, Row } from '@tanstack/react-table'
import { VirtualItem } from '@tanstack/react-virtual'
import { pick } from 'lodash-es'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { Foldout } from '@shared/components/Foldout'
import { Dialog } from '@shared/components/MaterialUIEnhancements/Dialog'
import { SearchBar } from '@shared/components/SearchBar'
import { SelectionToolbar, Table } from '@shared/components/Table'
import { ActionStrip, BasicListOperation } from '@shared/layouts'
import {
  useColumns,
  NewDialog,
  RowActions,
  useAsyncContentCaching,
  FilterPanel,
  SuccessPanelActions,
  SelectionToolbarActions,
  LifecyclePolicyDialog,
} from 'domains/Workspaces/Audio/Objects'
import { BodyCellLink } from 'layouts/BodyCellLink'
import { audioObjectsOverviewRoute } from 'pages/NWPlatform/Workspaces/Workspace/Audio/Objects'
import { audioObjectRawRoute } from 'pages/NWPlatform/Workspaces/Workspace/Audio/Objects/Object'
import { audioObjectQueryKeys } from 'services/queryKeys'

const OverviewRaw = () => {
  const { t } = useTranslation(['common', 'domain', 'entity'])
  const { enqueueSnackbar } = useSnackbar()
  const queryClient = useQueryClient()

  const navigate = useNavigate({ from: audioObjectsOverviewRoute.fullPath })
  const routeParams = audioObjectsOverviewRoute.useParams()
  const { query, groups, excludeGrouped } = audioObjectsOverviewRoute.useSearch()

  const tableInstance = useRef<ReactTableType<AudioObject>>(undefined)
  const virtualizerInstanceRef = useRef<VirtualItem[]>(undefined)

  const [newDialogOpen, setNewDialogOpen] = useState(false)
  const [loadingRows, setLoadingRows] = useState<string[]>([])
  const [filterPanelOpen, setFilterPanelOpen] = useState(false)
  const [objectData, setObjectData] = useState<AudioObject | AudioObject[] | undefined>(undefined)

  const { workspaceQuery, audiosQuery, setBatchGetEnabled } = useAsyncContentCaching({
    virtualItems: virtualizerInstanceRef.current,
    setLoadingRows,
  })

  const columns = useColumns('objects')

  const handleSuccess = useCallback(
    (data: AudioObject) => {
      enqueueSnackbar(`${t('domain:Workspace.Event.eventCreated', { name: data.name })}`, {
        description: `${t('domain:Workspace.Event.eventCreatedDescription')}`,
        variant: 'panel',
        persist: true,
        icon: 'success',
        Actions: (props) => <SuccessPanelActions {...props} {...routeParams} audioobjectId={data.id} />,
      })
    },
    [enqueueSnackbar, routeParams, t],
  )

  const handleLifecyclePolicyClick = useCallback((selectedRows: Row<AudioObject>[]) => {
    const objectData = selectedRows.map((row) => row.original)
    setObjectData(objectData)
  }, [])

  return (
    <>
      <ActionStrip>
        <SearchBar
          placeholder={t('domain:Workspace.Audio.searchAudioObject')}
          onSearch={({ search }) => navigate({ search: (prev) => ({ ...prev, query: search }) })}
          defaultValue={query}
        />

        <IconButton size="medium" onClick={() => setFilterPanelOpen(!filterPanelOpen)}>
          <FilterList fontSize="small" />
        </IconButton>

        <Foldout open={filterPanelOpen} onClose={() => setFilterPanelOpen(false)} placement="end" hideBackdrop={false}>
          <FilterPanel />
        </Foldout>

        <BasicListOperation
          refreshAction={useCallback(() => {
            queryClient.cancelQueries({ queryKey: audioObjectQueryKeys.batchGets() })
            setBatchGetEnabled(false)
            queryClient.invalidateQueries({
              queryKey: audioObjectQueryKeys.list({
                repositoryId: workspaceQuery.data?.contentRepositoryId,
                query,
                groups,
                excludeGrouped,
              }).queryKey,
            })
          }, [
            excludeGrouped,
            groups,
            query,
            queryClient,
            setBatchGetEnabled,
            workspaceQuery.data?.contentRepositoryId,
          ])}
          createAction={useCallback(() => {
            setNewDialogOpen(true)
            tableInstance.current?.resetRowSelection()
          }, [])}
        />
      </ActionStrip>

      <Table
        getTableInstance={(instance) => (tableInstance.current = instance)}
        size={48}
        data={audiosQuery.data?.flat ?? []}
        columns={columns}
        isInitialLoadingGlobal={workspaceQuery.isLoading || audiosQuery.isLoading}
        isInitialLoadingByRowByID={loadingRows.reduce((acc, row) => ({ ...acc, [row]: [] }), {})}
        enableLinking
        LinkProps={(row, content, cell) => (
          <BodyCellLink
            sx={[!!cell.column.columnDef.meta?.align && { justifyContent: cell.column.columnDef.meta.align }]}
            from={audioObjectsOverviewRoute.fullPath}
            to={audioObjectRawRoute.to}
            params={{ audioobjectId: row.original.id }}
          >
            {content}
          </BodyCellLink>
        )}
        excludeFieldsFromLinking={['id']}
        enableInfinity
        infinityProps={pick(audiosQuery, ['hasNextPage', 'isFetchingNextPage', 'fetchNextPage'])}
        enableVirtualization
        virtualizedProps={{
          getVirtualItems: (instance) => (virtualizerInstanceRef.current = instance),
        }}
        ActionMenuProps={(row) => ({
          title: row.original.name,
          entity: t('entity:content.audioObject', { count: 1 }),
        })}
        RowActions={(props) => <RowActions onClick={(row) => setObjectData(row.original)} {...props} />}
        renderSelectionToolbar={({ selectedRows }) => (
          <SelectionToolbar
            selectedRows={selectedRows}
            title={t('entity:content.audioObject', { count: 2 })}
            discardSelection={() => tableInstance.current?.resetRowSelection()}
            Actions={
              tableInstance.current && (
                <SelectionToolbarActions
                  onLifecyclePolicyClick={() => handleLifecyclePolicyClick(selectedRows)}
                  selectedRows={selectedRows}
                  tableInstance={tableInstance.current}
                />
              )
            }
          />
        )}
        tableOptions={{
          getRowId: (audio) => audio.id,
        }}
      />
      <Dialog open={newDialogOpen} onClose={() => setNewDialogOpen(false)}>
        <NewDialog onSuccess={handleSuccess} />
      </Dialog>

      <Dialog open={!!objectData} onClose={() => setObjectData(undefined)}>
        {objectData ? (
          <LifecyclePolicyDialog objectData={objectData} onSuccess={() => tableInstance.current?.resetRowSelection()} />
        ) : null}
      </Dialog>
    </>
  )
}

export const Overview = memo(OverviewRaw)
